export const LOGOUT_MARKER_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
export const LOGOUT_MARKER_COOKIE_NAME = 'vaultn_logout_at';
export const SESSION_STARTED_AT_STORAGE_KEY = 'vaultn.auth.sessionStartedAt';

function getCookieValue(cookieHeader: string, cookieName: string): string | null {
  const cookiePrefix = `${cookieName}=`;
  const matchingCookie = cookieHeader
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(cookiePrefix));

  if (!matchingCookie) {
    return null;
  }

  try {
    return decodeURIComponent(matchingCookie.slice(cookiePrefix.length));
  } catch {
    return null;
  }
}

function getDocumentCookie(): string {
  try {
    return globalThis.document?.cookie ?? '';
  } catch {
    return '';
  }
}

function setDocumentCookie(cookie: string): void {
  try {
    globalThis.document.cookie = cookie;
  } catch {
    // Logout marker persistence is best-effort; Auth0 logout still handles the active app.
  }
}

export function buildLogoutMarkerCookie(
  logoutAt: number,
  hostname = globalThis.location?.hostname ?? '',
  protocol = globalThis.location?.protocol ?? ''
): string {
  const cookieAttributes = [
    `${LOGOUT_MARKER_COOKIE_NAME}=${encodeURIComponent(String(logoutAt))}`,
    `Max-Age=${LOGOUT_MARKER_COOKIE_MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ];
  const cookieDomain = getLogoutMarkerCookieDomain(hostname);

  if (cookieDomain) {
    cookieAttributes.push(`Domain=${cookieDomain}`);
  }

  if (shouldUseSecureLogoutMarkerCookie(protocol)) {
    cookieAttributes.push('Secure');
  }

  return cookieAttributes.join('; ');
}

export function getLogoutMarkerCookieDomain(hostname = globalThis.location?.hostname ?? ''): string | undefined {
  return hostname.endsWith('.vaultn.com') ? '.vaultn.com' : undefined;
}

export function getLogoutMarkerCookieTimestamp(cookieHeader = getDocumentCookie()): number {
  return parseLogoutMarkerTimestamp(getCookieValue(cookieHeader, LOGOUT_MARKER_COOKIE_NAME));
}

export function parseLogoutMarkerTimestamp(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const timestamp = Number(value);

  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0;
}

export function shouldApplyLogoutMarker(logoutAt: number, sessionStartedAt: number): boolean {
  return logoutAt > 0 && sessionStartedAt > 0 && logoutAt > sessionStartedAt;
}

export function shouldUseSecureLogoutMarkerCookie(protocol = globalThis.location?.protocol ?? ''): boolean {
  return protocol === 'https:';
}

export function writeLogoutMarkerCookie(logoutAt = Date.now()): void {
  setDocumentCookie(buildLogoutMarkerCookie(logoutAt));
}
